import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import DeliveryForm, { type DeliveryFormData } from "./DeliveryForm";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

export default function DeliveryEdit() {
  const { deliveryId } = useParams({ strict: false }) as { deliveryId: string };
  const search = useSearch({ strict: false }) as { focus?: string };
  const focusParam =
    search?.focus ||
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("focus") || undefined
      : undefined);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: delivery, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["delivery", deliveryId],
    queryFn: async () => {
      return await trpcClient.deliveries.getById.query({ id: deliveryId });
    },
  });

  const handleSubmit = async (data: DeliveryFormData) => {
    try {
      await trpcClient.deliveries.update.mutate({
        id: deliveryId,
        data: data as any,
      });

      await queryClient.invalidateQueries({
        queryKey: ["delivery", deliveryId],
      });
      await queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      toast.success("Cập nhật phiếu giao hàng thành công");
      navigate({ to: `/delivery/${deliveryId}` as any, replace: true });
    } catch (e: any) {
      toast.error(e?.message || "Lỗi cập nhật");
    }
  };

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !delivery) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Giao hàng", to: "/delivery" }]}
        entityLabel="Phiếu giao hàng"
        backTo="/delivery"
        codeOrId={deliveryId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !delivery)
    return (
      <div className="w-full space-y-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Giao Hàng", to: "/delivery" },
        { label: delivery.deliveryId, to: `/delivery/${deliveryId}` },
        { label: "Chỉnh sửa" },
      ]}
      title={`Sửa ${delivery.deliveryId}`}
      onBack={() => navigate({ to: `/delivery/${deliveryId}` })}
      showContextPanel={false}
    >
      <div className="w-full p-4 animate-in fade-in duration-300">
        <DeliveryForm
          defaultValues={delivery as any}
          focusSection={focusParam === "location" ? "location" : undefined}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: `/delivery/${deliveryId}` })}
          submitLabel={(delivery as any)?.status === "DRAFT" ? "Lưu" : "Cập nhật"}
        />
      </div>
    </StandardDetailLayout>
  );
}
